import React from 'react'
import { Info } from 'react-feather'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldLabel as TwFieldLabel } from '@/components/ui/field'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useDialog from '@/hooks/useDialog'
import { cn } from '@/lib/utils'

interface Props {
  children: React.ReactNode
  htmlFor?: string
  className?: string
  wrapperClassName?: string
  tooltipHint?: TooltipHint
  modalHint?: ModalHint
}

export interface TooltipHint {
  tooltipTrigger?: string
  text: React.ReactElement | string
  className?: string
}

export interface ModalHint {
  modalTrigger: React.ReactNode
  modalTitle: string
  modalContent: React.ReactNode
}

const FieldLabel: React.FC<Props> = ({
  htmlFor,
  children,
  className,
  wrapperClassName: customWrapperClassName,
  tooltipHint,
  modalHint,
}) => {
  const {
    isShowingDialog: isShowingDialogHint,
    showDialog: showDialogHint,
    hideDialog: hideDialogHint,
  } = useDialog()

  const wrapperClassName = customWrapperClassName
    ? customWrapperClassName
    : 'inline-flex pb-1'

  const renderLabel = () => (
    <TwFieldLabel htmlFor={htmlFor} className={cn('mb-0', className)}>
      {children}
    </TwFieldLabel>
  )

  if (tooltipHint) {
    // Render with custom tooltip trigger text
    if (tooltipHint.tooltipTrigger) {
      return (
        <div
          className={cn(
            wrapperClassName,
            'flex-wrap gap-x-2 md:flex md:flex-row',
          )}
        >
          {renderLabel()}

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-primary mb-1 shrink-0 cursor-default md:mb-0">
                {tooltipHint?.tooltipTrigger}
              </div>
            </TooltipTrigger>

            <TooltipContent>{tooltipHint.text}</TooltipContent>
          </Tooltip>
        </div>
      )
    }

    // Render with default info mark tooltip
    return (
      <div className={cn(wrapperClassName, 'flex items-start')}>
        <TwFieldLabel htmlFor={htmlFor} className={cn('mb-0', className)}>
          <div>
            {children}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  className="text-primary ml-1 inline"
                  size={16}
                  style={{ marginTop: '-2px' }}
                />
              </TooltipTrigger>

              <TooltipContent>{tooltipHint.text}</TooltipContent>
            </Tooltip>
          </div>
        </TwFieldLabel>
      </div>
    )
  }

  // Render with tooltip that opens a modal
  if (modalHint) {
    return (
      <div className={cn(wrapperClassName, 'flex')}>
        {renderLabel()}

        <div>
          {typeof modalHint.modalTrigger === 'string' ? (
            <>
              <div
                role="button"
                className="text-primary cursor-pointer hover:underline md:mb-0
                  md:pl-2.5 text-xs! relative top-[1.5px]"
                onClick={showDialogHint}
              >
                {modalHint.modalTrigger}
              </div>
            </>
          ) : (
            <div onClick={showDialogHint}>{modalHint.modalTrigger}</div>
          )}

          <Dialog open={isShowingDialogHint} onHide={hideDialogHint}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{modalHint.modalTitle}</DialogTitle>
              </DialogHeader>

              <DialogBody>{modalHint.modalContent}</DialogBody>

              <DialogFooter onClick={hideDialogHint}>
                <Button variant="outline-secondary">Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    )
  }

  // Render without tooltips
  return <div className={cn(wrapperClassName, className)}>{renderLabel()}</div>
}

export default FieldLabel
